/*
 * BookList Component - TypeScript Migration
 * 
 * Main library container with custom hooks architecture:
 * ✅ useBookList, useBookFilters, useBookSelection, useBookPagination
 * ✅ Full TypeScript type safety
 */

import { useState, useCallback, MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import type { Session, Book, BookFilters } from '@/types'
import { supabase } from '../../lib/supabase'
import { searchBookInfo } from '../../utils/googleBooks'
import { searchOpenLibraryInfo } from '../../utils/openLibrary'
import { FIELD_LIMITS, truncateObjectFields } from '../../utils/validation'

// Import modals
import BookDetailModal from '../BookDetailModal'
import BulkEditModal from '../BulkEditModal'
import BatchAddListModal from '../BatchAddListModal'
import FetchReviewModal, { type ApplyUpdate } from '../FetchReviewModal'
import CustomConfirmModal from '../common/CustomConfirmModal'
import ConversationRequestModal from '../common/ConversationRequestModal'

// Import custom hooks
import { useBookList } from './hooks/useBookList'
import { useBookFilters } from './hooks/useBookFilters'
import { useBookSelection } from './hooks/useBookSelection'
import { useBookPagination } from './hooks/useBookPagination'
import { useBookExport } from './hooks/useBookExport'
import { useFieldVisibility } from './hooks/useFieldVisibility'

// Import UI components
import BookListPagination from './components/BookListPagination'
import BookListHeader from './components/BookListHeader'
import BookListSelectionToolbar from './components/BookListSelectionToolbar'
import BookListFilters from './components/BookListFilters'
import BookListGrid from './components/BookListGrid'
import ImportPreviewModal from './components/modals/ImportPreviewModal'

// Import utilities and helpers
import { formatAuthors } from './formattingUtils'

// Import constants and helpers
import { FORMAT_OPTIONS, STATUS_OPTIONS, PROGRESS_OPTIONS } from './constants'

import './BookList.css'

interface BookListProps {
    sharedUserId?: string | null
    onBack?: () => void
    isBiblioverso?: boolean
    session?: Session | null
}

interface FetchResultData {
    index: number
    currentBook: Book
    apiResults: APIResult[]
}

interface APIResult {
    source: 'google' | 'openlibrary'
    description?: string
    categories?: string[] | string
    page_count?: number | string
    publisher?: string
}

interface VisibleFields {
    [key: string]: boolean
}

type ViewMode = 'full-cards' | 'compact-cards' | 'compact-list' | 'extended-list'
type ConversationType = 'borrow' | 'comment'

export default function BookList({ sharedUserId, onBack, isBiblioverso = false, session = null }: Readonly<BookListProps>) {
    // ========================================================================
    // CUSTOM HOOKS INTEGRATION
    // ========================================================================

    const bookList = useBookList({ sharedUserId, isBiblioverso, session })
    const filters = useBookFilters({ isBiblioverso, books: bookList.books })
    const selection = useBookSelection()
    const pagination = useBookPagination()
    const exportImport = useBookExport({
        books: bookList.books,
        loadBooks: bookList.loadBooks,
        showNotify: bookList.showNotify,
    })
    const visibility = useFieldVisibility(!!sharedUserId, bookList.sharedSettings)

    // ========================================================================
    // LOCAL UI STATE
    // ========================================================================

    const [selectedBook, setSelectedBook] = useState<Book | Partial<Book> | null>(null)
    const [deletingId, setDeletingId] = useState<string | null>(null)

    const [fetchingInfo, setFetchingInfo] = useState(false)
    const [bulkFetchResults, setBulkFetchResults] = useState<FetchResultData[] | null>(null)
    const [pendingUpdates, setPendingUpdates] = useState<Record<string, unknown>>({})

    const [showConversationRequestModal, setShowConversationRequestModal] = useState(false)
    const [conversationRequestType, setConversationRequestType] = useState<ConversationType>('borrow')
    const [isSubmittingRequest, setIsSubmittingRequest] = useState(false)

    const [viewMode, setViewMode] = useState<ViewMode>('full-cards')
    const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
    const { t } = useTranslation()

    // ========================================================================
    // HELPER FUNCTIONS
    // ========================================================================

    const getVisibleFieldsForModal = (book: Book | Partial<Book>): VisibleFields | null => {
        if (!isBiblioverso && !sharedUserId) {
            return null
        }

        const ownerSettings = isBiblioverso ? bookList.biblioversoSettings[book.user_id || ''] : bookList.sharedSettings

        if (!ownerSettings || !Array.isArray(ownerSettings)) {
            return {
                title: true,
                author: true,
            }
        }

        const FIELD_NAME_MAPPINGS: Record<string, string[]> = {
            author: ['author'],
            category: ['category'],
            language: ['language'],
            publisher: ['publisher'],
            series: ['series'],
            series_number: ['series_number'],
            format: ['format'],
            status: ['status'],
            progress: ['progress'],
            location: ['location'],
            notes: ['notes'],
            synopsis: ['synopsis'],
            page_count: ['page_count'],
            created_at: ['created_at'],
            description: ['description'],
        }

        const visible: VisibleFields = {
            title: true,
            author: true,
        }

        ownerSettings.forEach((oldFieldName) => {
            const mappings = FIELD_NAME_MAPPINGS[oldFieldName]
            if (mappings) {
                mappings.forEach((fieldName) => {
                    visible[fieldName] = true
                })
            } else {
                visible[oldFieldName] = true
            }
        })

        return visible
    }

    // ========================================================================
    // Re-export hook functions for easier access in JSX
    // ========================================================================

    const {
        books,
        notification,
        setNotification,
        existingAuthors,
        existingPublishers,
        existingLocations,
        existingSeries,
        existingCategories,
        existingLanguages,
        sharedSettings,
        sharedProfile,
        // biblioversoSettings - unused
        loadBooks,
        deleteBook,
        showNotify,
        getUniqueBiblionautas,
    } = bookList

    const {
        filters: filterValues,
        visibleFilters,
        setVisibleFilters,
        showFilters,
        setShowFilters,
        showFilterSettings,
        setShowFilterSettings,
        searchTerm,
        setSearchTerm,
        setFilter,
        clearFilter,
        clearAllFilters,
        getFilteredBooks,
        getFilteredBooksExcluding,
    } = filters

    const {
        selectedIds,
        showBulkEditModal,
        setShowBulkEditModal,
        showBatchAddListModal,
        setShowBatchAddListModal,
        showBulkDeleteConfirm,
        setShowBulkDeleteConfirm,
        toggleSelection,
        selectAll,
        clearSelection,
        removeFromSelection,
    } = selection

    const {
        currentPage,
        itemsPerPage,
        sortConfig,
        changePage,
        changeItemsPerPage,
        resetPage,
        toggleSort,
        sortItems,
        paginate,
        getPaginationInfo,
    } = pagination

    // ========================================================================
    // COMPUTED VARIABLES
    // ========================================================================
    const filteredBooks = getFilteredBooks()
    const sortedBooks = sortItems(filteredBooks)
    const paginatedBooks = paginate(sortedBooks)
    const { totalPages, startIndex, endIndex } = getPaginationInfo(filteredBooks.length)
    const selectedCount = selectedIds.size
    const allFilteredSelected = filteredBooks.length > 0 && filteredBooks.every((book) => selectedIds.has(book.id))
    const hasActiveFilters = Object.values(filterValues).some((arr) => arr.length > 0)

    // ========================================================================
    // DYNAMIC FILTER FUNCTIONS
    // ========================================================================
    const getUniqueAuthors = (): string[] => [...new Set(getFilteredBooksExcluding('author').flatMap((b) => b.author || []))].filter(Boolean).sort((a, b) => a.localeCompare(b))
    const getUniqueFormats = (): readonly string[] => {
        const v = getFilteredBooksExcluding('format')
            .map((b) => b.format)
            .filter(Boolean)
        return v.length > 0 ? [...new Set(v)].sort((a, b) => a.localeCompare(b)) : FORMAT_OPTIONS
    }
    const getUniqueStatuses = (): readonly string[] => {
        const v = getFilteredBooksExcluding('status')
            .map((b) => b.status)
            .filter(Boolean)
        return v.length > 0 ? [...new Set(v)].sort((a, b) => a.localeCompare(b)) : STATUS_OPTIONS
    }
    const getUniqueProgress = (): readonly string[] => {
        const v = getFilteredBooksExcluding('progress')
            .map((b) => b.progress)
            .filter(Boolean)
        return v.length > 0 ? [...new Set(v)].sort((a, b) => a.localeCompare(b)) : PROGRESS_OPTIONS
    }
    const getUniquePublishers = () => [...new Set(getFilteredBooksExcluding('publisher').map((b) => b.publisher).filter(Boolean))].sort((a, b) => a.localeCompare(b))
    const getUniqueLocations = () => [...new Set(getFilteredBooksExcluding('location').map((b) => b.location).filter(Boolean))].sort((a, b) => a.localeCompare(b))
    const getUniqueSeries = () => [...new Set(getFilteredBooksExcluding('series').map((b) => b.series).filter(Boolean))].sort((a, b) => a.localeCompare(b))
    const getUniqueCategories = () => [...new Set(getFilteredBooksExcluding('category').flatMap((b) => b.category || []))].filter(Boolean).sort((a, b) => a.localeCompare(b))
    const getUniqueLanguages = () => [...new Set(getFilteredBooksExcluding('language').map((b) => b.language).filter(Boolean))].sort((a, b) => a.localeCompare(b))

    // ========================================================================
    // EVENT HANDLERS
    // ========================================================================
    const updateFilter = (filterName: string, values: string[]) => {
        setFilter(filterName as keyof BookFilters, values)
        resetPage()
    }
    const handleAddNewBook = () =>
        setSelectedBook({
            id: null as unknown as string,
            title: '',
            author: [],
            category: [],
            language: '',
            publisher: '',
            series: '',
            series_number: null,
            format: 'physical',
            status: 'owned',
            progress: 'not_read',
            location: '',
            notes: '',
            synopsis: '',
            description: '',
            page_count: null,
        })
    const handleBookClick = (book: Book) => setSelectedBook(book)
    const toggleBookSelection = (e: MouseEvent, bookId: string) => {
        e.stopPropagation()
        toggleSelection(bookId)
    }
    const selectAllFiltered = () => selectAll(filteredBooks.map((b) => b.id))
    const deselectAll = () => clearSelection()
    const handleSort = (key: keyof Book) => toggleSort(key)
    const handlePageChange = (page: number) => changePage(page)
    const handleItemsPerPageChange = (count: number) => changeItemsPerPage(count)
    const initiateDelete = (e: MouseEvent, id: string) => {
        e.stopPropagation()
        setConfirmDeleteId(id)
    }
    const confirmDelete = async () => {
        if (!confirmDeleteId) return
        setDeletingId(confirmDeleteId)
        setConfirmDeleteId(null)
        const success = await deleteBook(confirmDeleteId)
        if (success) {
            removeFromSelection(confirmDeleteId)
        }
        setDeletingId(null)
    }
    const cancelDelete = () => {
        setConfirmDeleteId(null)
        setShowBulkDeleteConfirm(false)
    }
    const confirmBulkDelete = async () => {
        const ids = Array.from(selectedIds)
        setShowBulkDeleteConfirm(false)
        for (const id of ids) {
            await deleteBook(id)
        }
        clearSelection()
        showNotify(t('bookList.notifications.booksDeleted.title'), t('bookList.notifications.booksDeleted.message', { count: ids.length }), 'success')
    }

    const handleBookUpdate = useCallback(
        async (updatedBook: Partial<Book>) => {
            if (updatedBook.id) {
                // Sanitize payload: remove internal fields
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { id, user_id, created_at, updated_at, profiles, ...updateData } = updatedBook as Book

                const { error } = await supabase
                    .from('books')
                    .update(truncateObjectFields(updateData, FIELD_LIMITS.books))
                    .eq('id', updatedBook.id)
                if (!error) {
                    setSelectedBook(null)
                    loadBooks() // Don't await here, closing modal is priority
                    showNotify(t('bookList.notifications.bookUpdated.title'), t('bookList.notifications.bookUpdated.message'), 'success')
                } else showNotify(t('bookList.notifications.bookUpdated.title'), t('bookList.notifications.bookUpdated.error'), 'danger')
            } else {
                const {
                    data: { user },
                } = await supabase.auth.getUser()
                if (!user) {
                    showNotify('Error', 'Tu sesión ha expirado o es inválida. Por favor, cierra sesión e inicia sesión de nuevo.', 'danger')
                    return
                }
                
                const { error } = await supabase
                    .from('books')
                    .insert([{ ...truncateObjectFields(updatedBook as Record<string, unknown>, FIELD_LIMITS.books), user_id: user.id }])
                if (!error) {
                    setSelectedBook(null)
                    loadBooks() // Don't await here
                    showNotify(t('bookList.notifications.bookAdded.title'), t('bookList.notifications.bookAdded.message'), 'success')
                } else showNotify(t('bookList.notifications.bookAdded.title'), t('bookList.notifications.bookAdded.error'), 'danger')
            }
        },
        [loadBooks, showNotify, t]
    )

    const handleBulkEdit = async (updates: Record<string, unknown>) => {
        const idsToUpdate = Array.from(selectedIds)
        const updateData = Object.fromEntries(Object.entries(updates).filter(([, v]) => v !== undefined && v !== ''))
        if (Object.keys(updateData).length === 0) {
            showNotify(t('bookList.notifications.noChanges.title'), t('bookList.notifications.noChanges.message'), 'warning')
            return
        }
        const truncated = truncateObjectFields(updateData, FIELD_LIMITS.books)
        for (const id of idsToUpdate) await supabase.from('books').update(truncated).eq('id', id)
        await loadBooks()
        setShowBulkEditModal(false)
        clearSelection()
        showNotify(t('bookList.notifications.bulkEditSuccess.title'), t('bookList.notifications.bulkEditSuccess.message', { count: idsToUpdate.length }), 'success')
    }

    const handleBatchAddToList = async (listId: string) => {
        const bookIds = Array.from(selectedIds)
        const items = bookIds.map((bookId) => ({
            list_id: listId,
            book_id: bookId,
        }))
        const { error } = await supabase.from('reading_list_items').insert(items)
        if (!error) {
            setShowBatchAddListModal(false)
            clearSelection()
            showNotify(t('bookList.notifications.addedToList.title'), t('bookList.notifications.addedToList.message', { count: bookIds.length }), 'success')
        } else {
            console.error('Error adding to list:', error)
            showNotify('Error', error.message || t('bookList.notifications.addedToList.error'), 'danger')
        }
    }

    const handleBulkFetchInfo = async () => {
        setFetchingInfo(true)
        const selectedBooks = books.filter((b) => selectedIds.has(b.id))
        const results: FetchResultData[] = []

        for (let i = 0
            ; i < selectedBooks.length; i++) {
            const book = selectedBooks[i]
            try {
                const googleInfo = await searchBookInfo(book.title, formatAuthors(book.author))
                const olInfo = await searchOpenLibraryInfo(book.title, formatAuthors(book.author))

                const apiResults: APIResult[] = []
                if (googleInfo) apiResults.push({ source: 'google', ...googleInfo })
                if (olInfo) apiResults.push({ source: 'openlibrary', ...olInfo })

                if (apiResults.length > 0) {
                    results.push({ index: i, currentBook: book, apiResults })
                }
            } catch (e) {
                console.error(`Error fetching info for ${book.title}:`, e)
            }
        }
        setFetchingInfo(false)
        if (results.length > 0) setBulkFetchResults(results)
        else showNotify(t('bookList.notifications.noResults.title'), t('bookList.notifications.noResults.message'), 'info')
    }

    const handleApplyBulkFetchResults = (updates: ApplyUpdate[]) => {
        const selectedBooks = books.filter((b) => selectedIds.has(b.id))
        const updatesMap: Record<string, unknown> = {}

        updates.forEach(({ index, updatedFields }) => {
            const book = selectedBooks[index]
            if (book) {
                updatesMap[book.id] = updatedFields
            }
        })

        setPendingUpdates((prev) => ({ ...prev, ...updatesMap }))
        setBulkFetchResults(null)
    }

    const handleSavePendingUpdates = async () => {
        for (const [bookId, updates] of Object.entries(pendingUpdates)) {
            await supabase
                .from('books')
                .update(truncateObjectFields(updates as Record<string, unknown>, FIELD_LIMITS.books))
                .eq('id', bookId)
        }
        await loadBooks()
        setPendingUpdates({})
        clearSelection()
        showNotify(t('bookList.notifications.changesSaved.title'), t('bookList.notifications.changesSaved.message'), 'success')
    }

    const handleOpenConversationRequest = (type: ConversationType) => {
        setConversationRequestType(type)
        setShowConversationRequestModal(true)
    }

    const handleConversationSubmit = async (message: string) => {
        setIsSubmittingRequest(true)
        const bookIds = Array.from(selectedIds)
        const bookTitles = books
            .filter((b) => bookIds.includes(b.id))
            .map((b) => b.title)
            .join(', ')
        const ownerId = sharedUserId || books.find((b) => bookIds.includes(b.id))?.user_id
        if (!ownerId) {
            showNotify(t('bookList.notifications.ownerError.title'), t('bookList.notifications.ownerError.message'), 'danger')
            setIsSubmittingRequest(false)
            return
        }
        const {
            data: { user },
        } = await supabase.auth.getUser()
        if (!user) return
        const requestMessage =
            conversationRequestType === 'borrow'
                ? t('bookList.conversation.borrowMessage', { titles: bookTitles, message })
                : t('bookList.conversation.commentMessage', { titles: bookTitles, message })
        const { data: conversation, error: convError } = await supabase
            .from('conversations')
            .insert([{ participant1_id: user.id, participant2_id: ownerId, type: conversationRequestType }])
            .select()
            .single()
        if (convError) {
            showNotify(t('bookList.notifications.conversationError.title'), t('bookList.notifications.conversationError.message'), 'danger')
            setIsSubmittingRequest(false)
            return
        }
        const { error: msgError } = await supabase.from('messages').insert([{ conversation_id: conversation.id, sender_id: user.id, content: requestMessage }])
        if (!msgError) {
            setShowConversationRequestModal(false)
            clearSelection()
            showNotify(t('bookList.notifications.requestSent.title'), t('bookList.notifications.requestSent.message'), 'success')
        } else showNotify(t('bookList.notifications.messageError.title'), t('bookList.notifications.messageError.message'), 'danger')
        setIsSubmittingRequest(false)
    }

    const handleExport = (format: 'csv' | 'json') => {
        const exportBooks = selectedIds.size > 0 ? books.filter((b) => selectedIds.has(b.id)) : filteredBooks

        exportImport.handleExport(format, viewMode, exportBooks, visibility.visibility)
    }

    return (
        <div className="booklist-container">
            <BookListHeader
                isSharedView={!!sharedUserId}
                isBiblioverso={isBiblioverso}
                sharedProfile={sharedProfile}
                onBack={onBack}
                filteredCount={filteredBooks.length}
                totalCount={books.length}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                viewMode={viewMode}
                setViewMode={setViewMode}
                showFilters={showFilters}
                setShowFilters={setShowFilters}
                hasActiveFilters={hasActiveFilters}
                onAddBook={handleAddNewBook}
            />

            {/* Filter Bar */}
            {showFilters && (
                <BookListFilters
                    filterValues={filterValues}
                    updateFilter={updateFilter}
                    clearFilter={clearFilter}
                    clearAllFilters={clearAllFilters}
                    hasActiveFilters={hasActiveFilters}
                    visibleFilters={visibleFilters}
                    setVisibleFilters={setVisibleFilters}
                    showFilterSettings={showFilterSettings}
                    setShowFilterSettings={setShowFilterSettings}
                    getUniqueLanguages={getUniqueLanguages}
                    getUniqueCategories={getUniqueCategories}
                    getUniqueAuthors={getUniqueAuthors}
                    getUniqueSeries={getUniqueSeries}
                    getUniquePublishers={getUniquePublishers}
                    getUniqueFormats={getUniqueFormats}
                    getUniqueStatuses={getUniqueStatuses}
                    getUniqueProgress={getUniqueProgress}
                    getUniqueLocations={getUniqueLocations}
                    getUniqueBiblionautas={getUniqueBiblionautas}
                    isBiblioverso={isBiblioverso}
                    isSharedView={!!sharedUserId}
                    sharedSettings={sharedSettings}
                />
            )}

            {/* Selection Toolbar */}
            {selectedCount > 0 && (
                <BookListSelectionToolbar
                    selectedCount={selectedCount}
                    onDeselectAll={deselectAll}
                    isSharedView={!!sharedUserId}
                    isBiblioverso={isBiblioverso}
                    onBulkEdit={() => setShowBulkEditModal(true)}
                    onBulkFetchInfo={handleBulkFetchInfo}
                    fetchingInfo={fetchingInfo}
                    onBatchAddToList={() => setShowBatchAddListModal(true)}
                    onBulkDelete={() => setShowBulkDeleteConfirm(true)}
                    pendingUpdatesCount={Object.keys(pendingUpdates).length}
                    onSavePendingUpdates={handleSavePendingUpdates}
                    onOpenConversationRequest={handleOpenConversationRequest}
                />
            )}

            {filteredBooks.length > 0 ? (
                <>
                    <BookListGrid
                        viewMode={viewMode}
                        books={paginatedBooks}
                        selectedIds={selectedIds}
                        toggleBookSelection={toggleBookSelection}
                        onBookClick={handleBookClick}
                        initiateDelete={initiateDelete}
                        isBiblioverso={isBiblioverso}
                        isSharedView={!!sharedUserId}
                        deletingId={deletingId}
                        sharedSettings={sharedSettings}
                        allFilteredSelected={allFilteredSelected}
                        deselectAll={deselectAll}
                        selectAllFiltered={selectAllFiltered}
                        sortConfig={sortConfig}
                        handleSort={handleSort}
                        visibility={visibility.visibility}
                        toggleField={visibility.toggleField}
                        resetToDefaults={visibility.resetToDefaults}
                        allowedFields={visibility.allowedFields}
                    />

                    <BookListPagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        itemsPerPage={itemsPerPage}
                        onPageChange={handlePageChange}
                        onItemsPerPageChange={handleItemsPerPageChange}
                        startIndex={startIndex}
                        endIndex={endIndex}
                        totalItems={filteredBooks.length}
                        showExport={!isBiblioverso && !sharedUserId && (viewMode === 'compact-list' || viewMode === 'extended-list')}
                        selectedCount={selectedIds.size}
                        filteredCount={filteredBooks.length}
                        showExportOptions={exportImport.showExportOptions}
                        setShowExportOptions={exportImport.setShowExportOptions}
                        onExport={handleExport}
                        onImportClick={exportImport.handleImportClick}
                        onFileUpload={exportImport.handleFileUpload}
                        fileInputRef={exportImport.fileInputRef}
                    />
                </>
            ) : (
                <div className="empty-state animate-fade-in-up">
                    <div className="empty-illustration">{searchTerm || hasActiveFilters ? '🔍' : '📚'}</div>
                    <h3 className="empty-title">{searchTerm || hasActiveFilters ? t('bookList.empty.noBooksFound') : t('bookList.empty.libraryEmpty')}</h3>
                    <p className="empty-description">
                        {searchTerm || hasActiveFilters
                            ? t('bookList.empty.searchHint')
                            : t('bookList.empty.startHint')}
                    </p>
                    {(searchTerm || hasActiveFilters) && (
                        <button
                            className="btn-secondary"
                            onClick={() => {
                                setSearchTerm('')
                                clearAllFilters()
                            }}
                        >
                            {t('bookList.empty.clearFilters')}
                        </button>
                    )}
                </div>
            )}

            <ConversationRequestModal
                isOpen={showConversationRequestModal}
                onClose={() => setShowConversationRequestModal(false)}
                onSubmit={handleConversationSubmit}
                title={conversationRequestType === 'borrow' ? t('bookList.modals.borrow') : t('bookList.modals.comment')}
                books={filteredBooks.filter((b) => selectedIds.has(b.id))}
                type={conversationRequestType}
                submitting={isSubmittingRequest}
            />

            <ImportPreviewModal
                isOpen={exportImport.showImportPreview}
                onClose={() => exportImport.setShowImportPreview(false)}
                importData={exportImport.importData}
                updateImportItem={exportImport.updateImportItem}
                removeImportItem={exportImport.removeImportItem}
                handleImportConfirm={exportImport.handleImportConfirm}
                isImporting={exportImport.isImporting}
                books={books}
                existingAuthors={existingAuthors}
                existingPublishers={existingPublishers}
                existingLocations={existingLocations}
                existingSeries={existingSeries}
                existingCategories={existingCategories}
                existingLanguages={existingLanguages}
                FORMAT_OPTIONS={FORMAT_OPTIONS}
                STATUS_OPTIONS={STATUS_OPTIONS}
                PROGRESS_OPTIONS={PROGRESS_OPTIONS}
            />

            {/* Fetch Review Modal */}
            {bulkFetchResults && <FetchReviewModal results={bulkFetchResults} onClose={() => setBulkFetchResults(null)} onApply={handleApplyBulkFetchResults} />}

            {/* Bulk Edit Modal */}
            {showBulkEditModal && (
                <BulkEditModal
                    selectedCount={selectedCount}
                    onClose={() => setShowBulkEditModal(false)}
                    onApply={handleBulkEdit}
                    existingAuthors={existingAuthors}
                    existingPublishers={existingPublishers}
                    existingLocations={existingLocations}
                    existingSeries={existingSeries}
                    existingCategories={existingCategories}
                    existingLanguages={existingLanguages}
                />
            )}

            {/* Batch Add List Modal */}
            {showBatchAddListModal && (
                <BatchAddListModal selectedCount={selectedIds.size} onClose={() => setShowBatchAddListModal(false)} onConfirm={handleBatchAddToList} />
            )}

            {/* Book Detail Modal */}
            {selectedBook && (
                <BookDetailModal
                    book={selectedBook as Book}
                    onClose={() => setSelectedBook(null)}
                    onSave={handleBookUpdate}
                    existingAuthors={existingAuthors}
                    existingPublishers={existingPublishers}
                    existingLocations={existingLocations}
                    existingSeries={existingSeries}
                    existingCategories={existingCategories}
                    existingLanguages={existingLanguages}
                    readOnly={isBiblioverso || !!sharedUserId}
                    ownerName={isBiblioverso ? (selectedBook.profiles?.full_name || 'Mi Biblioteca') : sharedProfile?.full_name || sharedProfile?.email}
                    visibleFields={getVisibleFieldsForModal(selectedBook)}
                />
            )}

            {/* Custom Confirmation Modals */}
            <CustomConfirmModal
                isOpen={!!confirmDeleteId}
                type="danger"
                title={t('bookList.modals.deleteBook.title')}
                message={t('bookList.modals.deleteBook.message')}
                confirmText={t('bookList.modals.deleteBook.confirm')}
                onConfirm={confirmDelete}
                onCancel={cancelDelete}
            />

            <CustomConfirmModal
                isOpen={showBulkDeleteConfirm}
                type="danger"
                title={t('bookList.modals.deleteBooks.title')}
                message={t('bookList.modals.deleteBooks.message', { count: selectedIds.size })}
                confirmText={t('bookList.modals.deleteBooks.confirm')}
                onConfirm={confirmBulkDelete}
                onCancel={cancelDelete}
            />

            <CustomConfirmModal
                isOpen={notification.isOpen}
                type={notification.type}
                title={notification.title}
                message={notification.message}
                showCancel={false}
                confirmText={t('bookList.modals.deleteBook.understood', { defaultValue: 'Entendido' })} 
                onConfirm={() => setNotification({ ...notification, isOpen: false })}
                data-testid="notification-modal" // Added for E2E testing
            />
        </div>
    )
}
